"use strict";

customElements.define('ec-cpm-tooltip', class extends HTMLElement {
  constructor() {
    super();
    this.triggerBtn = null;
    this.tooltipContentEl = null;
    this.containerEl = null;
    this.mediaQuery = window.matchMedia('(max-width: 1023px)');
    this.resizeObserver = null;
    this.isOpen = false;
    this.lastEnterTime = 0;
    this.gracePeriod = 500;
    this._updatePosition = this.throttle(this._updatePosition.bind(this), 16);
    this.handleMediaChange = this.handleMediaChange.bind(this);
    this.handleToggle = this.handleToggle.bind(this);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.closeOnOutsideClick = this.closeOnOutsideClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }
  throttle(func, delay) {
    let lastCall = 0;
    let timeoutId = null;
    return (...args) => {
      const now = Date.now();
      const remaining = delay - (now - lastCall);
      if (remaining <= 0) {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        lastCall = now;
        func.apply(this, args);
      } else if (!timeoutId) {
        timeoutId = setTimeout(() => {
          lastCall = Date.now();
          timeoutId = null;
          func.apply(this, args);
        }, remaining);
      }
    };
  }
  connectedCallback() {
    this.triggerBtn = this.querySelector('button');
    this.tooltipContentEl = this.querySelector('.ec-cpm-tooltip__content');
    this.containerEl = this.parentElement;
    if (!this.triggerBtn || !this.tooltipContentEl) return;
    this.triggerBtn.setAttribute('aria-expanded', 'false');
    this.triggerBtn.addEventListener('click', this.handleToggle);
    document.addEventListener('click', this.closeOnOutsideClick);
    document.addEventListener('keydown', this.handleKeyDown);
    this.mediaQuery.addEventListener('change', this.handleMediaChange, {
      passive: true
    });
    this.handleMediaChange(this.mediaQuery);
  }
  disconnectedCallback() {
    this.removeEventListeners();
    if (this.triggerBtn) {
      this.triggerBtn.removeEventListener('click', this.handleToggle);
      this.triggerBtn.removeEventListener('mouseenter', this.handleMouseEnter);
      this.triggerBtn.removeEventListener('mouseleave', this.handleMouseLeave);
    }
    document.removeEventListener('click', this.closeOnOutsideClick);
    document.removeEventListener('keydown', this.handleKeyDown);
    this.mediaQuery.removeEventListener('change', this.handleMediaChange);
  }
  setOpenState(newState) {
    if (this.isOpen === newState) return;
    this.isOpen = newState;
    this.triggerBtn.setAttribute('aria-expanded', this.isOpen ? 'true' : 'false');
    if (this.isOpen) {
      this.classList.add('ec-cpm--is-open');
      this._updatePosition();
    } else {
      this.classList.remove('ec-cpm--is-open');
    }
  }
  handleMouseEnter() {
    this.lastEnterTime = Date.now();
    this.setOpenState(true);
  }
  handleMouseLeave() {
    this.setOpenState(false);
  }
  handleToggle(e) {
    e.stopPropagation();
    const clickTime = Date.now();
    const timeSinceEnter = clickTime - this.lastEnterTime;
    if (!this.mediaQuery.matches && timeSinceEnter < this.gracePeriod) {
      return;
    }
    this.setOpenState(!this.isOpen);
  }
  handleKeyDown(e) {
    if (e.key === 'Escape' && this.isOpen) {
      this.setOpenState(false);
      this.triggerBtn.focus();
    }
  }
  closeOnOutsideClick(e) {
    if (this.isOpen && !this.contains(e.target)) {
      this.setOpenState(false);
    }
  }
  handleMediaChange(mediaQueryList) {
    if (mediaQueryList.matches) {
      this.triggerBtn.removeEventListener('mouseenter', this.handleMouseEnter);
      this.triggerBtn.removeEventListener('mouseleave', this.handleMouseLeave);
      this.addEventListeners();
      if (this.isOpen) setTimeout(() => this._updatePosition(), 50);
    } else {
      this.triggerBtn.addEventListener('mouseenter', this.handleMouseEnter);
      this.triggerBtn.addEventListener('mouseleave', this.handleMouseLeave);
      this.removeEventListeners();
      if (this.tooltipContentEl) {
        this.tooltipContentEl.style.left = '';
        this.tooltipContentEl.style.setProperty('--arrow-position', '');
      }
    }
  }
  addEventListeners() {
    window.addEventListener('resize', this._updatePosition, {
      passive: true
    });
    if (!this.resizeObserver && this.tooltipContentEl) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.isOpen) this._updatePosition();
      });
      this.resizeObserver.observe(this.tooltipContentEl);
      if (this.containerEl) this.resizeObserver.observe(this.containerEl);
    }
  }
  removeEventListeners() {
    window.removeEventListener('resize', this._updatePosition);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }
  _updatePosition() {
    if (!this.mediaQuery.matches || !this.tooltipContentEl || !this.triggerBtn || !this.isOpen) return;
    let contentRect = this.tooltipContentEl.getBoundingClientRect();
    const sourceRect = this.triggerBtn.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const margin = 10;
    const arrowBuffer = 15;
    if (contentRect.width === 0) return;
    let newLeft = parseFloat(window.getComputedStyle(this.tooltipContentEl).left) || 0;
    if (contentRect.left < margin) {
      newLeft += margin - contentRect.left;
    } else if (contentRect.right > viewportWidth - margin) {
      newLeft -= contentRect.right - (viewportWidth - margin);
    }
    this.tooltipContentEl.style.left = `${newLeft}px`;
    contentRect = this.tooltipContentEl.getBoundingClientRect();
    const iconCenter = sourceRect.left + sourceRect.width / 2;
    const rawArrow = iconCenter - contentRect.left;
    if (rawArrow < arrowBuffer) {
      newLeft -= arrowBuffer - rawArrow;
    } else if (rawArrow > contentRect.width - arrowBuffer) {
      newLeft += rawArrow - (contentRect.width - arrowBuffer);
    }
    this.tooltipContentEl.style.left = `${newLeft}px`;
    const finalRect = this.tooltipContentEl.getBoundingClientRect();
    const finalArrow = iconCenter - finalRect.left;
    this.tooltipContentEl.style.setProperty('--arrow-position', `${finalArrow}px`);
  }
});